import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './ShopsCategoryFilter.styles';
import { CategoriesIcon, CloseIcon } from '@/components/UI/Icons/Icons';
import { BottomSheet } from '@/components/UI/BottomSheet/BottomSheet';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { Category } from '@/api/types/categories.types';
import { useResponsive } from '@/hooks/useResponsive';
import { useCategories } from '@/hooks/useCategories';

function CategoryDropdownItem({
  category,
  getIconSrc,
  onSelect,
}: {
  category: Category;
  getIconSrc: (icon: string | undefined) => string;
  onSelect: () => void;
}) {
  const [iconError, setIconError] = React.useState(false);
  const src = getIconSrc(category.icon);

  return (
    <S.CategoryMenuItem type="button" onClick={onSelect}>
      <S.CategoryItemIconWrap>
        {!iconError && src ? (
          <S.CategoryItemIcon
            src={src}
            alt=""
            loading="lazy"
            onError={() => setIconError(true)}
          />
        ) : (
          <S.CategoryItemIconFallback>
            <CategoriesIcon />
          </S.CategoryItemIconFallback>
        )}
      </S.CategoryItemIconWrap>
      <S.CategoryItemName>{category.name}</S.CategoryItemName>
    </S.CategoryMenuItem>
  );
}

export interface ShopsCategoryFilterProps {
  onCategoryChange?: (category: Category | null) => void;
}

export const ShopsCategoryFilter: React.FC<ShopsCategoryFilterProps> = ({
  onCategoryChange,
}) => {
  const { t } = useTranslation();
  const { isDesktop } = useResponsive();
  const [isCategoriesOpen, toggleCategories] = useToggleOpenState();

  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null
  );

  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const { categories, loading: categoriesLoading } = useCategories(true);

  const getCategoryIconSrc = useMemo(
    () =>
      (icon: string | undefined): string => {
        if (!icon) return '';
        if (icon.startsWith('http') || icon.startsWith('/')) return icon;
        if (icon.startsWith('categories/')) return `/${icon}`;
        return `/categories/${icon}`;
      },
    []
  );

  const handleClickOutsideCategory = useCallback(
    (e: MouseEvent) => {
      const target = e.target as Node;
      const el = categoryDropdownRef.current;
      const parent = el?.parentElement;

      if (el && parent && !parent.contains(target) && !el.contains(target)) {
        toggleCategories(false);
      }
    },
    [toggleCategories]
  );

  useEffect(() => {
    if (isCategoriesOpen && isDesktop) {
      document.addEventListener('mousedown', handleClickOutsideCategory);
      return () =>
        document.removeEventListener('mousedown', handleClickOutsideCategory);
    }
  }, [isCategoriesOpen, isDesktop, handleClickOutsideCategory]);

  const clearCategory = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = null;
    setSelectedCategory(next);
    onCategoryChange?.(next);
  };

  const handleCategorySelect = (cat: Category) => {
    setSelectedCategory(cat);
    onCategoryChange?.(cat);
    toggleCategories(false);
  };

  return (
    <>
      <S.CategoryControlWrap
        $isActive={isCategoriesOpen}
        ref={categoryDropdownRef}
      >
        <S.Control
          size="s"
          appearance="defaultOutlined"
          onClick={toggleCategories}
          data-stat="shops-category-filter"
          $categoryOpen={isCategoriesOpen}
          $categoryHasValue={Boolean(selectedCategory)}
        >
          {selectedCategory ? (
            <>
              <S.CategoryImage
                src={getCategoryIconSrc(selectedCategory.icon)}
                alt={selectedCategory.name ?? ''}
                loading="lazy"
              />
              {selectedCategory.name}
              <CloseIcon onClick={clearCategory} />
            </>
          ) : (
            t('pages.shops.category')
          )}
        </S.Control>

        {isDesktop && isCategoriesOpen && (
          <S.CategoryDropdownPanel
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
          >
            {categoriesLoading ? (
              <S.CategoryMenu>
                {Array.from({ length: 6 }).map((_, i) => (
                  <S.CategoryMenuItemSkeleton key={i} />
                ))}
              </S.CategoryMenu>
            ) : (
              <S.CategoryMenu>
                {(categories ?? []).map(cat => (
                  <CategoryDropdownItem
                    key={cat.id}
                    category={cat}
                    getIconSrc={getCategoryIconSrc}
                    onSelect={() => handleCategorySelect(cat)}
                  />
                ))}
              </S.CategoryMenu>
            )}
          </S.CategoryDropdownPanel>
        )}
      </S.CategoryControlWrap>

      {!isDesktop && (
        <BottomSheet
          isOpen={isCategoriesOpen}
          close={() => toggleCategories(false)}
          title={t('pages.shops.category')}
          isInPortal
        >
          {categoriesLoading ? (
            <S.CategoryMenu>
              {Array.from({ length: 6 }).map((_, i) => (
                <S.CategoryMenuItemSkeleton key={i} />
              ))}
            </S.CategoryMenu>
          ) : (
            <S.CategoryMenu>
              {(categories ?? []).map(cat => (
                <CategoryDropdownItem
                  key={cat.id}
                  category={cat}
                  getIconSrc={getCategoryIconSrc}
                  onSelect={() => handleCategorySelect(cat)}
                />
              ))}
            </S.CategoryMenu>
          )}
        </BottomSheet>
      )}
    </>
  );
};
